import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useCare } from '../../contexts/CareContext'
import { useToast, humanizeError } from '../shared/Toast'

// Lightweight, guaranteed-to-work fallback if the user prefers a form
// (or Ginie is unreachable). Creates the recipient + an initial care plan.
export default function ManualOnboardingForm({ onBack }) {
  const { user } = useAuth()
  const { reload } = useCare()
  const toast = useToast()
  const navigate = useNavigate()

  const [fullName, setFullName] = useState('')
  const [preferredName, setPreferredName] = useState('')
  const [dob, setDob] = useState('')
  const [careStage, setCareStage] = useState('unknown')
  const [conditions, setConditions] = useState('')
  const [goals, setGoals] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit() {
    if (busy) return
    if (!fullName.trim()) {
      toast.error('Please enter their name.')
      return
    }
    setBusy(true)
    try {
      const { data: recipient, error } = await supabase
        .from('care_recipients')
        .insert({
          created_by: user.id,
          full_name: fullName.trim(),
          preferred_name: preferredName.trim() || null,
          date_of_birth: dob || null,
          conditions: conditions
            .split(',')
            .map((c) => c.trim())
            .filter(Boolean),
          care_stage: careStage,
        })
        .select()
        .single()
      if (error) throw error

      if (goals.trim()) {
        const nextReview = new Date()
        nextReview.setDate(nextReview.getDate() + 90)
        const { error: pErr } = await supabase.from('care_plans').insert({
          care_recipient_id: recipient.id,
          created_by: user.id,
          care_stage: careStage,
          current_priorities: goals.trim(),
          next_review_date: nextReview.toISOString().slice(0, 10),
          is_active: true,
        })
        if (pErr) throw pErr
      }

      await reload()
      toast.success(`${preferredName || fullName}'s care profile is ready.`)
      navigate('/', { replace: true })
    } catch (err) {
      toast.error(humanizeError(err))
      setBusy(false)
    }
  }

  return (
    <div className="screen">
      <div className="container page" style={{ paddingTop: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, padding: 0 }}>
          ← Back to Ginie
        </button>
        <div className="stack mt-16">
          <h1 className="title">Set up manually</h1>
          <div className="field">
            <label className="label">Full name *</label>
            <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Margaret Chen" />
          </div>
          <div className="field">
            <label className="label">Preferred name</label>
            <input className="input" value={preferredName} onChange={(e) => setPreferredName(e.target.value)} placeholder="e.g. Mom" />
          </div>
          <div className="field">
            <label className="label">Date of birth</label>
            <input type="date" className="input" value={dob} onChange={(e) => setDob(e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Care stage</label>
            <select className="select" value={careStage} onChange={(e) => setCareStage(e.target.value)}>
              <option value="early">Early</option>
              <option value="middle">Middle</option>
              <option value="late">Late</option>
              <option value="unknown">Not sure</option>
            </select>
          </div>
          <div className="field">
            <label className="label">Conditions</label>
            <input className="input" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="Comma separated" />
          </div>
          <div className="field">
            <label className="label">Care goals</label>
            <textarea className="textarea" value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="What does good care look like for them?" />
          </div>
          <button className="btn btn-lg btn-block" onClick={submit} disabled={busy || !fullName.trim()}>
            {busy ? <span className="spinner" /> : 'Create care profile'}
          </button>
        </div>
      </div>
    </div>
  )
}
